import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import SupplierSelector from "../../components/CustomComponents/modal/SupplierSelector";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import PurchaseOrderTabBar from "./purchaseOrderTabBar";
import swal from "sweetalert";
import "./updatePurchaseOrder.css";

const UpdatePurchaseOrder = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [poData, setPoData] = useState(null);

  // Form fields
  const [poName, setPoName] = useState("");
  const [deliveryDueDate, setDeliveryDueDate] = useState("");
  const [deliveryType, setDeliveryType] = useState("Standard");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [poStatus, setPoStatus] = useState("Draft");
  const [paymentStatus, setPaymentStatus] = useState("Unpaid");
  const [poAmount, setPoAmount] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [otherExpenses, setOtherExpenses] = useState("0");
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [deliverToCompId, setDeliverToCompId] = useState("");

  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  // Fetch purchase order data
  const fetchPurchaseOrder = useCallback(async () => {
    if (!poId || !compID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_BY_ID}?poId=${poId}&compId=${compID}`);

      if (response.data && response.data.data) {
        const data = response.data.data;
        setPoData(data);

        // Populate form fields
        setPoName(data.pO_Name || "");
        setDeliveryDueDate(data.deliveryDueDate ? data.deliveryDueDate.split('T')[0] : "");
        setDeliveryType(data.pO_DeliveryType || "Standard");

        // Set selected supplier if supplier data exists
        if (data.supplierID && data.supplierName) {
          setSelectedSupplier({
            s_ID: data.supplierID,
            s_Name: data.supplierName,
            // Include other supplier fields if available
            s_Email: data.supplierEmail || "",
            s_Contact: data.supplierContact || ""
          });
        }

        setPoStatus(data.pO_status || "Draft");
        setPaymentStatus(data.pO_Paymentstatus || "Unpaid");
        setPoAmount(data.pO_Amount?.toString() || "");
        setPaidAmount(data.pO_PaidAmount?.toString() || "");
        setOtherExpenses(data.pO_OtherExpenses?.toString() || data.otherExpenses?.toString() || "0");
        setIsSendEmail(data.pO_IsSendEmail || false);
        setDeliverToCompId(data.deliverStockToCompID || "");
      }
    } catch (error) {
      console.error("Error fetching purchase order:", error);
      showErrorToast("Failed to load purchase order");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [poId, compID]);

  useEffect(() => {
    fetchPurchaseOrder();
  }, [fetchPurchaseOrder]);

  // Handle Other Expenses change - trigger distribution to items
  const handleOtherExpensesChange = async (newValue) => {
    // Debounce: Wait for user to finish typing before distributing
    // The distribution will happen when the form is saved
    // For now, just update the state
  };

  // Check if costs have been calculated for all items
  const checkCostsCalculated = useCallback(async () => {
    if (!poId || !compID) return false;

    try {
      // Fetch all purchase order items
      const response = await axiosInstance.get(`${APIs.GET_PURCHASE_ORDER_PRODUCTS}?poId=${poId}&compId=${compID}`);
      const items = response.data?.data || response.data || [];
      
      if (!items || items.length === 0) {
        return false; // No items to check
      }

      // Check if all items have UnitCost calculated (not just SupplierPrice)
      // UnitCost should be different from SupplierPrice if expenses exist, or equal if no expenses
      // But the key is that UnitCost should be set (not null/undefined/0)
      const itemsWithoutCost = items.filter(item => {
        const unitCost = parseFloat(item.unitCost || item.UnitCost || 0);
        const supplierPrice = parseFloat(item.supplierPrice || item.SupplierPrice || 0);
        // Consider cost calculated if UnitCost is set and > 0
        // Or if UnitCost equals SupplierPrice (meaning costs were calculated but no expenses)
        return unitCost <= 0 && supplierPrice > 0;
      });

      return itemsWithoutCost.length === 0;
    } catch (error) {
      console.error("Error checking costs:", error);
      return false;
    }
  }, [poId, compID]);

  // Handle receiving purchase order - add stock to inventory
  const handleReceivePurchaseOrder = useCallback(async () => {
    if (!poId || !compID) return;

    // Check if costs have been calculated before allowing receive
    const costsCalculated = await checkCostsCalculated();
    if (!costsCalculated) {
      const result = await swal({
        title: "Costs Not Calculated",
        text: "Please calculate cost prices for all products before receiving the purchase order. Would you like to go to the Items page to calculate costs?",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Go to Items Page",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        },
        dangerMode: false,
      });
      
      if (result) {
        // Navigate to items page
        navigate(`/purchaseOrder/updatePurchaseOrderItems/${poId}`);
      }
      return;
    }

    try {
      setLoading(true);

      // Call API to receive PO and add stock
      const response = await axiosInstance.post(
        `${APIs.POST_RECEIVE_PURCHASE_ORDER}?poId=${poId}&compId=${compID}`
      );

      if (response.data && response.data.status) {
        const itemsReceived = response.data.data?.ItemsReceived || 0;
        showSuccessToast(
          `Purchase order received successfully! Stock added for ${itemsReceived} item(s).`
        );
        
        // Refresh PO data to show updated status
        await fetchPurchaseOrder();
      } else {
        showErrorToast(response.data?.message || "Failed to receive purchase order");
      }
    } catch (error) {
      console.error("Error receiving purchase order:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to receive purchase order"
      );
    } finally {
      setLoading(false);
    }
  }, [poId, compID, fetchPurchaseOrder, checkCostsCalculated]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!poName.trim()) {
      showErrorToast("Please enter purchase order name");
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        PO_ID: parseInt(poId),
        PO_Name: poName,
        DeliveryDueDate: deliveryDueDate ? new Date(deliveryDueDate).toISOString() : null,
        PO_DeliveryType: deliveryType,
        SupplierID: selectedSupplier ? parseInt(selectedSupplier.s_ID || selectedSupplier.Supplier_ID) : null,
        SupplierName: selectedSupplier ? (selectedSupplier.s_Name || selectedSupplier.Supplier_Name) : "",
        PO_status: poStatus,
        PO_Paymentstatus: paymentStatus,
        PO_Quantity: poData?.pO_Quantity || "0",
        PO_Amount: poData?.pO_Amount ? parseFloat(poData.pO_Amount) : (poAmount ? parseFloat(poAmount) : 0), // Use existing PO amount from data
        PO_PaidAmount: poData?.pO_PaidAmount ? parseFloat(poData.pO_PaidAmount) : (paidAmount ? parseFloat(paidAmount) : 0), // Use existing paid amount from data
        OtherExpenses: parseFloat(otherExpenses || 0),
        PO_IsSendEmail: isSendEmail,
        DeliverStockToCompID: deliverToCompId ? parseInt(deliverToCompId) : null,
        StaffUserName: currentUserName || "System",
        CompID: compID,
        CreatedBy: poData?.createdBy || poData?.CreatedBy || currentUserName || "System",
        ModifiedBy: currentUserName || "System",
        PurchaseOrderPayments: [], // Empty array for payments
        PurchaseOrderProductLists: [] // Empty array for product lists
      };

      await axiosInstance.put(APIs.PUT_UPDATE_PURCHASE_ORDER, updateData);

      // If status is being changed to "Received", add stock to inventory
      if (poStatus === "Received" && poData?.pO_status !== "Received") {
        // Check costs before allowing status change to Received
        const costsCalculated = await checkCostsCalculated();
        if (!costsCalculated) {
          showErrorToast("Cannot set status to 'Received'. Please calculate cost prices for all products first.");
          // Reset status to previous value
          setPoStatus(poData?.pO_status || "Draft");
          return;
        }
        await handleReceivePurchaseOrder();
      } else {
        showSuccessToast("Purchase order updated successfully");
        fetchPurchaseOrder(); // Refresh data
      }
    } catch (error) {
      console.error("Error updating purchase order:", error);
      showErrorToast("Failed to update purchase order");
    } finally {
      setLoading(false);
    }
  };

  // Basic Information Panel
  const renderBasicInfoPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-info-circle me-2"></i>
        Basic Information
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-md-6">
            <FormInput
              label="Purchase Order Name *"
              type="text"
              value={poName}
              onchange={(e) => setPoName(e.target.value)}
              placeHolder="Enter PO name"
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Delivery Due Date"
              type="date"
              value={deliveryDueDate}
              onchange={(e) => setDeliveryDueDate(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Delivery Type
              </label>
              <select
                className="form-select"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Standard">Standard Delivery</option>
                <option value="Express">Express Delivery</option>
                <option value="Pickup">Pickup</option>
              </select>
            </div>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  // Supplier Information Panel
  const renderSupplierInfoPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-truck me-2"></i>
        Supplier Information
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-12">
            <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              Supplier *
            </label>
            <SupplierSelector
              selectedSupplier={selectedSupplier}
              setSelectedSupplier={setSelectedSupplier}
              showModal={showSupplierModal}
              setShowModal={setShowSupplierModal}
              setLoading={setLoading}
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  // Order Details Panel
  const renderOrderDetailsPanel = () => (
    <Panel className="mb-4">
      <PanelHeader
        style={{
          backgroundColor: themeColor,
          color: 'white',
          border: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 20px',
          fontWeight: '600',
          fontSize: '16px'
        }}
      >
        <i className="fas fa-file-alt me-2"></i>
        Order Details
      </PanelHeader>
      <PanelBody
        style={{
          padding: '25px',
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          borderRadius: '0 0 10px 10px',
          border: `1px solid ${themeColor}20`
        }}
      >
        <div className="row g-4">
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Status
              </label>
              <select
                className="form-select"
                value={poStatus}
                onChange={(e) => setPoStatus(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Draft">Draft</option>
                <option value="Pending">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Ordered">Ordered</option>
                <option value="Received">Received</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Payment Status
              </label>
              <select
                className="form-select"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#2d3436' : '#F9F9F9',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px'
                }}
              >
                <option value="Unpaid">Unpaid</option>
                <option value="Partial">Partial Payment</option>
                <option value="Paid">Fully Paid</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Total Amount (Auto-calculated)
              </label>
              <div
                className="form-control"
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#1a1a1a' : '#f5f5f5',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px',
                  cursor: 'not-allowed'
                }}
              >
                ${parseFloat(poAmount || 0).toFixed(2)}
              </div>
              <small className="text-muted" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                This amount is automatically calculated from purchase order items
              </small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Paid Amount (Read-only)
              </label>
              <div
                className="form-control"
                style={{
                  borderRadius: "8px",
                  border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                  background: isDarkMode ? '#1a1a1a' : '#f5f5f5',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  padding: '8px 12px',
                  cursor: 'not-allowed'
                }}
              >
                ${parseFloat(paidAmount || 0).toFixed(2)}
              </div>
              <small className="text-muted" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                This field is managed through payment records
              </small>
            </div>
          </div>
          <div className="col-md-6">
            <FormInput
              label="Other Expenses"
              type="number"
              step="0.01"
              value={otherExpenses}
              onchange={(e) => {
                setOtherExpenses(e.target.value);
                // Trigger recalculation of UnitCost for all items
                handleOtherExpensesChange(e.target.value);
              }}
              placeHolder="0.00"
              helpText="Shipping, handling, etc. (distributed proportionally based on supplier price)"
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  if (initialLoading) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/purchaseOrder/managePurchaseOrders">Purchase Orders</Link>
          </li>
          <li className="breadcrumb-item active">Update Purchase Order</li>
        </ol>
        <h1 className="page-header mb-0">
          {poData ? `PO-${poData.pO_ID}: ${poData.pO_Name}` : "Update Purchase Order"}
        </h1>
      </div>
      <div className="ms-auto" />
    </div>
  );

  const btnBase = {
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.3s ease",
  };

  return (
    <div className="container-fluid p-3">
      {loading && !initialLoading && <Preloader />}
      {renderBreadcrumb()}

      {poId && (
        <>
          <PurchaseOrderTabBar poId={poId} />
          <div className="d-flex align-items-center justify-content-end gap-2 mb-3 px-3 update-po-actions">
            <button
              onClick={() => navigate("/purchaseOrder/managePurchaseOrders")}
              className="btn btn-rounded"
              style={{
                ...btnBase,
                backgroundColor: "#6c757d",
                background: "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)",
                boxShadow: "0 2px 8px rgba(108, 117, 125, 0.4)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #5a6268 0%, #6c757d 100%)";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.6)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(108, 117, 125, 0.4)";
              }}
            >
              Cancel
            </button>
            <button
              onClick={(e) => handleSubmit(e)}
              className="btn btn-rounded"
              disabled={loading}
              style={{
                ...btnBase,
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            {poStatus !== "Received" && (
              <button
                onClick={async () => {
                  const result = await swal({
                    title: "Receive Purchase Order?",
                    text: "This will add stock to inventory for all items in this purchase order. Are you sure?",
                    icon: "warning",
                    buttons: {
                      cancel: { text: "Cancel", value: false, visible: true, closeModal: true },
                      confirm: { text: "Yes, Receive PO", value: true, visible: true, closeModal: true },
                    },
                    dangerMode: true,
                  });
                  if (result) await handleReceivePurchaseOrder();
                }}
                className="btn btn-rounded"
                disabled={loading}
                style={{
                  ...btnBase,
                  backgroundColor: "#28a745",
                  background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  boxShadow: "0 2px 8px rgba(40, 167, 69, 0.4)",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = "linear-gradient(135deg, #20c997 0%, #28a745 100%)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(40, 167, 69, 0.6)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #28a745 0%, #20c997 100%)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(40, 167, 69, 0.4)";
                }}
              >
                <i className="fas fa-check-circle me-2"></i>
                Receive PO
              </button>
            )}
          </div>
        </>
      )}

      {/* Basic Information Panel */}
      {renderBasicInfoPanel()}

      {/* Supplier Information Panel */}
      {renderSupplierInfoPanel()}

      {/* Order Details Panel */}
      {renderOrderDetailsPanel()}
    </div>
  );
};

export default UpdatePurchaseOrder;